import { useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addActionNameToMutationResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/microsoft_login';

const MicrosoftLoginSchema = z.object({
  loginUri: z.string(),
});

type MicrosoftSharedMailboxLoginVariables = { sharedMailbox: string };

export async function loadMicrosoftSharedMailboxLoginUriQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: MicrosoftSharedMailboxLoginVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: variables });

  const parseResult = MicrosoftLoginSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'microsoftLogin');
    throw new SilentError();
  }

  return parseResult.data.loginUri;
}

export function useLoadMicrosoftSharedMailboxLoginUri() {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<string, ApiError, MicrosoftSharedMailboxLoginVariables>({
    mutationFn: variables => loadMicrosoftSharedMailboxLoginUriQueryFn(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    string,
    ApiError,
    MicrosoftSharedMailboxLoginVariables,
    'loadMicrosoftSharedMailboxLoginUri'
  >('loadMicrosoftSharedMailboxLoginUri', mutation);
}
